import * as TreatmentPlan from "./tplan/tplan";
import { getBoundingCone, getPHISignedURLS3 } from "./backend_api";
import {
    clientErrorMessages,
    EMPTY_STRING,
    pageLoadStates,
    refFrames,
    sceneOrientations,
    structureColorPalette,
    volumeNames,
} from "../constants";
import StructureModel from "../components/__shared__/Viewers/Structures/PatientAnatomy/StructureModel";
import { rootStore } from "../redux/store";
import { setErrorState } from "../redux/error_banner/actions";
import { getMRIURLsHelper } from "./threejs_helpers";
import BiopsyCoreModel from "../components/__shared__/Viewers/Structures/PatientAnatomy/BiopsyCoreModel";
import BoundingCone from "../components/__shared__/Viewers/Structures/Other/BoundingCone";
import ProstateAxes from "../components/__shared__/Viewers/Structures/Other/ProstateAxes";
import { getSignedUrl, toArray } from "./helpers";
import ProstateModel from "../components/__shared__/Viewers/Structures/PatientAnatomy/ProstateModel";
import ROIModel from "../components/__shared__/Viewers/Structures/PatientAnatomy/ROIModel";
import { DEFAULT_TICK_PERCENT } from "../components/__shared__/SliceControl/consts";
import { trackingEventNames } from "./pendo_constants";
import { prostateSegOptions } from "../components/CreateTPlan_2SegMRI/constants";

export function loadProstate({
    plan,
    ids,
    patientAnatomyModel,
    createBoundingCone,
    createAxes,
    setLoadState,
}) {
    const tPlan = plan.TreatmentPlan;
    const coreStructureMRIFrame = TreatmentPlan.hasCoreStructure(tPlan)
        ? TreatmentPlan.getStructureInFrame(
              TreatmentPlan.getCoreStructure(tPlan),
              refFrames.MRI
          )
        : undefined;

    // A Koelis biopsy's core structure only gains an MRI frame once TRUS-to-MRI
    // fusion has completed; skip loading MR_PROSTATE rather than fetching a URI
    // that hasn't been written yet (e.g. only the AI-generated segmentation exists).
    if (coreStructureMRIFrame && coreStructureMRIFrame.URI !== EMPTY_STRING) {
        return getPHISignedURLS3({
            ...ids,
            uris: toArray(coreStructureMRIFrame.URI),
        })
            .then((payload) => payload.json())
            .then((json) => {
                const url = getSignedUrl(json.payload.signedurls);
                const prostateStructure = new ProstateModel();

                prostateStructure.mriViewer = patientAnatomyModel.mriViewer;
                prostateStructure.meshViewer = patientAnatomyModel.meshViewer;
                return prostateStructure
                    .initializeView(url, {
                        name: volumeNames.MR_PROSTATE,
                        color: structureColorPalette.PROSTATE,
                        opacity: 0.4,
                        visibility: true,
                    })
                    .then(() => {
                        patientAnatomyModel.prostate = prostateStructure;
                        if (createBoundingCone) {
                            loadBoundingCone({
                                ids,
                                plan,
                                patientAnatomyModel,
                            });
                        }
                        if (createAxes) {
                            const prostateAxes = new ProstateAxes();
                            prostateStructure.initProstateAxes(prostateAxes);
                        }
                    });
            })
            .catch(() => {
                setLoadState(pageLoadStates.FAILED);
                rootStore.dispatch(
                    setErrorState(
                        true,
                        trackingEventNames.LOAD_PROSTATE,
                        plan.MetaData.PlanUUID,
                        clientErrorMessages.PROSTATE_LOAD_FAILED
                    )
                );
            });
    }
}

export function loadAllProstateSegmentations(
    scope,
    plan,
    ids,
    createAxes,
    onLoadCallback = undefined,
    selectedSegmentation
) {
    let tplan = plan.TreatmentPlan;
    let saveData = plan.SavedData;

    let patientAnatomyModel;
    if (scope.patientAnatomyModel) {
        patientAnatomyModel = scope.patientAnatomyModel;
    } else if (scope.props.patientAnatomyModel) {
        patientAnatomyModel = scope.props.patientAnatomyModel;
    }

    if (TreatmentPlan.hasCoreStructure(tplan)) {
        let aiSegmentationURI;
        if (
            saveData.AIEditedProstateURI &&
            saveData.AIEditedProstateURI !== EMPTY_STRING
        ) {
            aiSegmentationURI = saveData.AIEditedProstateURI;
        } else if (
            saveData.AIProstateURI &&
            saveData.AIProstateURI !== EMPTY_STRING
        ) {
            aiSegmentationURI = saveData.AIProstateURI;
        }

        let uploadedSegmentationURI;
        if (
            saveData.UploadedEditedProstateURI &&
            saveData.UploadedEditedProstateURI !== EMPTY_STRING
        ) {
            uploadedSegmentationURI = saveData.UploadedEditedProstateURI;
        } else if (
            saveData.UploadedProstateURI &&
            saveData.UploadedProstateURI !== EMPTY_STRING
        ) {
            uploadedSegmentationURI = saveData.UploadedProstateURI;
        } else if (
            TreatmentPlan.isKoelisSegmentationProvidedWithoutFusion(plan)
        ) {
            uploadedSegmentationURI = TreatmentPlan.getStructureInFrame(
                TreatmentPlan.getCoreStructure(tplan),
                refFrames.MRI
            ).URI;
        }

        if (
            uploadedSegmentationURI &&
            uploadedSegmentationURI !== EMPTY_STRING
        ) {
            if (!patientAnatomyModel.prostateUploaded) {
                getPHISignedURLS3({
                    ...ids,
                    uris: toArray(uploadedSegmentationURI),
                })
                    .then((payload) => payload.json())
                    .then((json) => {
                        let url = getSignedUrl(json.payload.signedurls);
                        let prostateStructure = new ProstateModel();

                        prostateStructure.mriViewer =
                            patientAnatomyModel.mriViewer;
                        prostateStructure.meshViewer =
                            patientAnatomyModel.meshViewer;

                        prostateStructure
                            .initializeView(url, {
                                name: volumeNames.MR_PROSTATE_USER_UPLOAD,
                                color: structureColorPalette.PROSTATE,
                                opacity: 0.3,
                            })
                            .then(() => {
                                patientAnatomyModel.prostateUploaded =
                                    prostateStructure;
                                patientAnatomyModel.crm = prostateStructure.crm;

                                if (createAxes) {
                                    let prostateAxes = new ProstateAxes();
                                    prostateStructure.initProstateAxes(
                                        prostateAxes
                                    );
                                }

                                prostateStructure.visibility =
                                    selectedSegmentation !==
                                    prostateSegOptions.AI_SEG;

                                if (onLoadCallback) {
                                    onLoadCallback();
                                }
                            });
                    })
                    .catch(() => {
                        scope.setState({ loadState: pageLoadStates.FAILED });
                    });
            }
        }

        if (aiSegmentationURI && aiSegmentationURI !== EMPTY_STRING) {
            if (!patientAnatomyModel.prostateAI) {
                getPHISignedURLS3({
                    ...ids,
                    uris: toArray(aiSegmentationURI),
                })
                    .then((payload) => payload.json())
                    .then((json) => {
                        let url = getSignedUrl(json.payload.signedurls);
                        let prostateStructure = new ProstateModel();

                        prostateStructure.mriViewer =
                            patientAnatomyModel.mriViewer;
                        prostateStructure.meshViewer =
                            patientAnatomyModel.meshViewer;

                        prostateStructure
                            .initializeView(url, {
                                name: volumeNames.MR_PROSTATE_AI,
                                color: structureColorPalette.AI_PROSTATE,
                                opacity: 0.3,
                                visibility:
                                    selectedSegmentation ===
                                        prostateSegOptions.AI_SEG ||
                                    !uploadedSegmentationURI ||
                                    uploadedSegmentationURI === EMPTY_STRING,
                            })
                            .then(() => {
                                patientAnatomyModel.prostateAI =
                                    prostateStructure;
                                patientAnatomyModel.crm = prostateStructure.crm;

                                if (createAxes) {
                                    let prostateAxes = new ProstateAxes();
                                    prostateStructure.initProstateAxes(
                                        prostateAxes
                                    );
                                }

                                if (onLoadCallback) {
                                    onLoadCallback();
                                }
                            });
                    })
                    .catch(() => {
                        scope.setState({ loadState: pageLoadStates.FAILED });
                    });
            }
        }
    }
}

export function loadROIs({
    plan,
    ids,
    patientAnatomyModel,
    patientAnatomyController,
    onLoadCallback,
}) {
    const tPlan = plan.TreatmentPlan;
    if (TreatmentPlan.hasROIs(tPlan)) {
        TreatmentPlan.getROIs(tPlan).forEach((roi, idx) => {
            const roiFrame = TreatmentPlan.getStructureInFrame(
                roi,
                refFrames.MRI
            );

            if (roiFrame && roiFrame.URI !== EMPTY_STRING) {
                getPHISignedURLS3({ ...ids, uris: toArray(roiFrame.URI) })
                    .then((payload) => payload.json())
                    .then((json) => {
                        const url = getSignedUrl(json.payload.signedurls);
                        const roiModel = new ROIModel(roi);
                        roiModel.mriViewer = patientAnatomyModel.mriViewer;
                        roiModel.meshViewer = patientAnatomyModel.meshViewer;
                        roiModel.initializeView(url, {
                            name: volumeNames.MR_ROI + String(idx),
                            color: structureColorPalette.ROI_CS_CANCER,
                            opacity: 1.0,
                            visibility: true,
                        });

                        patientAnatomyModel.addRoi(roiModel);
                        if (patientAnatomyController) {
                            patientAnatomyController.update();
                        }
                        if (onLoadCallback) {
                            onLoadCallback();
                        }
                    })
                    .catch(() => {
                        rootStore.dispatch(
                            setErrorState(
                                true,
                                trackingEventNames.LOAD_ROI,
                                plan.MetaData.PlanUUID,
                                clientErrorMessages.MESH_DOWNLOAD_FAILED
                            )
                        );
                    });
            }
        });
    }
}

export function loadOtherStructures({
    plan,
    ids,
    patientAnatomyModel,
    onLoadCallback = undefined,
}) {
    const tPlan = plan.TreatmentPlan;
    if (TreatmentPlan.hasOtherStructures(tPlan)) {
        TreatmentPlan.getOtherStructures(tPlan).forEach((os, idx) => {
            const osFrame = TreatmentPlan.getStructureInFrame(
                os,
                refFrames.MRI
            );
            if (osFrame && osFrame.URI !== EMPTY_STRING) {
                return getPHISignedURLS3({
                    ...ids,
                    uris: toArray(
                        TreatmentPlan.getStructureInFrame(os, refFrames.MRI).URI
                    ),
                })
                    .then((payload) => payload.json())
                    .then((json) => {
                        const url = getSignedUrl(json.payload.signedurls);
                        const osModel = new ROIModel(os);
                        osModel.mriViewer = patientAnatomyModel.mriViewer;
                        osModel.meshViewer = patientAnatomyModel.meshViewer;
                        osModel.initializeView(url, {
                            name: volumeNames.MR_TUMOR + String(idx),
                            color: structureColorPalette.TUMOR_GROUND_TRUTH,
                            visibility: false,
                            opacity: 0.5,
                        });

                        patientAnatomyModel.addTumorGroundTruth(osModel);

                        if (onLoadCallback) {
                            onLoadCallback();
                        }
                    })
                    .catch(() => {
                        rootStore.dispatch(
                            setErrorState(
                                true,
                                clientErrorMessages.MESH_DOWNLOAD_FAILED
                            )
                        );
                    });
            }
        });
    }
}

export function loadMargin({ plan, ids, patientAnatomyModel, setLoadState }) {
    const tPlan = plan.TreatmentPlan;
    if (TreatmentPlan.hasMargins(tPlan)) {
        const margin = tPlan.StructureData.Margins[0];
        getPHISignedURLS3({
            ...ids,
            uris: toArray(
                TreatmentPlan.getStructureInFrame(margin, refFrames.MRI).URI
            ),
        })
            .then((payload) => payload.json())
            .then((json) => {
                const url = getSignedUrl(json.payload.signedurls);
                const marginStructure = new StructureModel();
                marginStructure.mriViewer = patientAnatomyModel.mriViewer;
                marginStructure.meshViewer = patientAnatomyModel.meshViewer;
                marginStructure.initializeView(url, {
                    name: volumeNames.MR_MARGIN_AI,
                    color: structureColorPalette.TX_MARGIN,
                    opacity: 0.3,
                });
                marginStructure.visibility = true;
                patientAnatomyModel.margin = marginStructure;
            })
            .catch(() => {
                setLoadState(pageLoadStates.FAILED);
                rootStore.dispatch(
                    setErrorState(
                        true,
                        clientErrorMessages.MESH_DOWNLOAD_FAILED
                    )
                );
            });
    }
}

export async function loadMri({
    ids,
    currentTreatmentPlan,
    mriModel,
    loadState,
    setLoadState,
    sceneOrientation,
}) {
    const tPlan = currentTreatmentPlan.TreatmentPlan;
    return getMRIURLsHelper(tPlan, ids)
        .then((mriUrls) => {
            mriModel.displayMeshContours = true;
            if (!currentTreatmentPlan.SavedData.CPMForLUTDisplayURI) {
                // Disable LUT if we don't have a CPMForLUTDisplayURI defined
                mriModel.lutEnabled = false;
            }

            return mriModel.initializeView(mriUrls);
        })
        .then(() => {
            // Set up connection between CRMModel and MRIModel so that the CRMModel can get access to the MRIModel
            // MRIModel has reference to MRIViewer (mriModel._mriViewer) to create the scene, but the
            // MRIViewer need access to MRIModel to call its methods
            if (mriModel._mriViewer) {
                mriModel._mriViewer.mriModel = mriModel;
            }

            // Sets camera position and orientation
            mriModel.orientation = sceneOrientation ?? sceneOrientations.AXIAL;
            mriModel.sliceIndexPercent = DEFAULT_TICK_PERCENT;

            if (loadState !== pageLoadStates.FAILED) {
                setLoadState(pageLoadStates.COMPLETE);
                mriModel._meshViewer.controls.saveState();
            }

            if (
                mriModel.lutEnabled &&
                currentTreatmentPlan.SavedData.CPMForLUTDisplayURI
            ) {
                getPHISignedURLS3({
                    token: ids.token,
                    useruuid: ids.useruuid,
                    patientuuid: ids.patientuuid,
                    planuuid: ids.planuuid,
                    uris: toArray(
                        currentTreatmentPlan.SavedData.CPMForLUTDisplayURI
                    ),
                })
                    .then((payload) => payload.json())
                    .then((json) => getSignedUrl(json.payload.signedurls))
                    .then((url) => mriModel.initializeLUT([url]));
            }
        })
        .catch(() => {
            setLoadState(pageLoadStates.FAILED);
            rootStore.dispatch(
                setErrorState(
                    true,
                    trackingEventNames.LOAD_MRI,
                    ids.planuuid,
                    clientErrorMessages.MRI_LOAD_FAILED
                )
            );
        });
}

export function loadBxCores({ plan, patientAnatomyModel }) {
    const tPlan = plan.TreatmentPlan;
    const cores = TreatmentPlan.getBiopsyCores(tPlan);
    if (cores) {
        for (const [idx, c] of cores.entries()) {
            // Koelis cores are recorded in TRUS space and only gain an MRI
            // frame once TRUS-to-MRI fusion has run; skip rendering them
            // until then rather than crashing the whole load chain.
            if (!TreatmentPlan.getStructureInFrame(c, refFrames.MRI)) {
                continue;
            }

            // This helps highlight the cores on hover at step 2, 4, 5
            c.coreIndex = idx;
            let coreModel = new BiopsyCoreModel();
            coreModel.mriViewer = patientAnatomyModel.mriViewer;
            coreModel.meshViewer = patientAnatomyModel.meshViewer;
            coreModel.initializeView(c);
            coreModel.visibility = true;
            patientAnatomyModel.addBxCore(coreModel);
        }
    }
}

export function loadBoundingCone({ ids, plan, patientAnatomyModel }) {
    const tPlan = plan.TreatmentPlan;

    if (TreatmentPlan.hasBxCores(tPlan)) {
        const bxCores = TreatmentPlan.getBiopsyCores(tPlan);
        const prostate = patientAnatomyModel.prostate;
        getBoundingCone({
            token: ids.token,
            sphere: {
                radius: prostate.boundingSphere.radius,
                center: {
                    X: prostate.boundingSphere.center.x,
                    Y: prostate.boundingSphere.center.y,
                    Z: prostate.boundingSphere.center.z,
                },
            },
            bx: bxCores,
        })
            .then((result) => {
                const boundingCone = result.payload;
                const boundingConeStructure = new BoundingCone();

                boundingConeStructure.mriViewer = patientAnatomyModel.mriViewer;
                boundingConeStructure.meshViewer =
                    patientAnatomyModel.meshViewer;
                boundingConeStructure.initializeView(boundingCone);

                boundingConeStructure.visibility = false;
            })
            .catch(() => {});
    }
}

export function resetViewer(meshViewer, mriViewer, sliceController, mriModel) {
    // Reset 3D Mesh Viewer
    meshViewer.current.resetScene();

    // Reset 2D MRI Viewer
    mriViewer.current.resetScene();

    // Reset MRI slice to default value
    sliceController.current.reset();

    // Reset controls to match with the selected orientation
    // The first mriModel.orientation is a "set" function
    // While the second mriModel.orientation is a "get" function
    // eslint-disable-next-line
    mriModel.orientation = mriModel.orientation;
}
